import {endOfQuarter, formatISO, getQuarter, getYear, startOfQuarter, subQuarters} from "date-fns";

import type {IFetchContext} from "@pg-mono/data-fetcher";
import {consoleError} from "@pg-mono/logger";
import {createRequestActionTypes} from "@pg-mono/request-state";
import {PRICES_CITIES} from "@pg-rp/navigation";

import {isServer} from "../../../app/read_rp_environment_variables";
import type {IRPStore} from "../../../app/rp_reducer";
import type {IRPRequestMeta} from "../../../app/rp_request_meta";
import {disableServerCache} from "../../../app/state/server_cache_slice";
import {notifyBugsnag} from "../../../errors/bugsnag/notify_bugsnag";
import {IAppDispatch} from "../../../store/store";
import {getRcnDataApi} from "../api/get_rcn_data_api";
import {getPricesCityRegionId} from "../constants/prices_cities_regions";
import {
    TRANSACTION_PRICES_GRAIN,
    TRANSACTION_PRICES_MARKET,
    TRANSACTION_PRICES_METRIC,
    TRANSACTION_PRICES_MIN_TRANSACTIONS,
    TRANSACTION_PRICES_QUARTERS_LAG,
    TRANSACTION_PRICES_UNIT
} from "../constants/transaction_prices";
import type {ITransactionPrice} from "../types/ITransactionPrice";
import type {RcnPriceStatsFilters} from "../types/RcnPriceStatsQuery";
import type {RcnPriceStatsSeriesPoint} from "../types/RcnPriceStatsSeriesPoint";
import type {RcnRegionsPriceStatsQuery} from "../types/RcnRegionsPriceStats";
import {getPricesAvailableDate} from "../utils/get_prices_available_date";

const FETCH_TRANSACTION_PRICES = "reports/FETCH_TRANSACTION_PRICES";
export const fetchTransactionPricesTypes = createRequestActionTypes(FETCH_TRANSACTION_PRICES);

/**
 * Bounds of the single quarter the table reports: TRANSACTION_PRICES_QUARTERS_LAG quarters back from the quarter
 * of `date` - getPricesAvailableDate, so the quarter always lies inside BigData's offer history. The window is
 * that one quarter, so every city is read for the same span or not at all.
 */
export const getTransactionPricesDateRange = (date: Date): {date_from: string; date_to: string} => {
    const targetStart = subQuarters(startOfQuarter(date), TRANSACTION_PRICES_QUARTERS_LAG);

    return {
        date_from: formatISO(targetStart, {representation: "date"}),
        date_to: formatISO(endOfQuarter(targetStart), {representation: "date"})
    };
};

// The same quarter as an RCN period ("2026-04-01", "2026-Q2"), for views that pick the reported point out of a
// longer series - the city page reads this quarter too, so its figures match the hub table's row.
export const getTransactionPricesPeriod = (date: Date): {period_start: string; period_label: string} => {
    const targetStart = subQuarters(startOfQuarter(date), TRANSACTION_PRICES_QUARTERS_LAG);

    return {
        period_start: formatISO(targetStart, {representation: "date"}),
        period_label: `${getYear(targetStart)}-Q${getQuarter(targetStart)}`
    };
};

const isValidPoint = (point: unknown): point is RcnPriceStatsSeriesPoint => {
    if (typeof point !== "object" || point === null) {
        return false;
    }

    const candidate = point as Partial<RcnPriceStatsSeriesPoint>;

    return typeof candidate.period_start === "string" && typeof candidate.period_label === "string" && Number.isFinite(Number(candidate.value));
};

export const toTransactionPrice = (slug: string, regionId: number, point: RcnPriceStatsSeriesPoint): ITransactionPrice => ({
    slug,
    regionId,
    periodStart: point.period_start,
    periodLabel: point.period_label,
    medianPriceM2: Number(point.value),
    unit: TRANSACTION_PRICES_UNIT,
    transactionsCount: Number(point.n_rows) || 0,
    changePct: point.change_pct === null || point.change_pct === undefined ? null : Number(point.change_pct)
});

// The reported period, once it clears the sample floor. The series holds at most the one period the
// window asked for, so this is really a quality gate: too thin, and the city is left out entirely.
export const getLatestSettledPoint = (series: RcnPriceStatsSeriesPoint[]): RcnPriceStatsSeriesPoint | null => {
    const settled = series.filter((point) => isValidPoint(point) && Number(point.n_rows) >= TRANSACTION_PRICES_MIN_TRANSACTIONS);

    return settled.reduce<RcnPriceStatsSeriesPoint | null>(
        (latest, point) => (latest === null || point.period_start > latest.period_start ? point : latest),
        null
    );
};

// The reported quarter's params, shared by the hub table and the city page's single-region reads, so both
// show the same figure for a city. No rooms filter: the registry publishes an all-rooms aggregate, which rests
// on every sale in the period instead of one size's slice and matches the city-wide offer average.
export const getTransactionPricesBaseQuery = (date: Date): RcnPriceStatsFilters => ({
    grain: TRANSACTION_PRICES_GRAIN,
    market: TRANSACTION_PRICES_MARKET,
    unit: TRANSACTION_PRICES_UNIT,
    metric: TRANSACTION_PRICES_METRIC,
    order: "desc",
    // The window spans exactly one period, so there is at most one point to return.
    limit: 1,
    ...getTransactionPricesDateRange(date)
});

// regionIds must arrive sorted - they form part of the RTK Query cache key.
export const getTransactionPricesQuery = (regionIds: number[], date: Date): RcnRegionsPriceStatsQuery => ({
    regionIds,
    ...getTransactionPricesBaseQuery(date)
});

/**
 * Reads the reported quarter for every city from RCN and replaces the store slice the hub table renders.
 * Subscriptions are skipped - no component reads these entries, the derived slice below is what renders.
 */
export const loadTransactionPrices = (referenceDate: Date) => async (dispatch: IAppDispatch) => {
    dispatch({type: fetchTransactionPricesTypes.start});

    const cities = PRICES_CITIES.map((city) => ({slug: city.slug, regionId: getPricesCityRegionId(city.slug)})).filter(
        (city): city is typeof city & {regionId: number} => city.regionId !== null
    );
    // Deduplicated and sorted so the cache key is stable no matter how PRICES_CITIES is ordered.
    const regionIds = [...new Set(cities.map((city) => city.regionId))].sort((prev, next) => prev - next);

    try {
        const response = await dispatch(
            getRcnDataApi.endpoints.getRcnRegionsData.initiate(getTransactionPricesQuery(regionIds, referenceDate), {subscribe: false})
        ).unwrap();

        const seriesByRegion = new Map(response.results.map((result) => [result.region_id, result.series]));
        const result: ITransactionPrice[] = [];

        for (const city of cities) {
            // A region the registry has nothing for still comes back, with an empty series.
            const latestPoint = getLatestSettledPoint(seriesByRegion.get(city.regionId) ?? []);

            if (latestPoint) {
                result.push(toTransactionPrice(city.slug, city.regionId, latestPoint));
            }
        }

        dispatch({type: fetchTransactionPricesTypes.success, result});
    } catch (error) {
        consoleError("loadTransactionPrices error: ", error);
        notifyBugsnag(error, "loadTransactionPrices", {regionIds});
        dispatch(disableServerCache());

        // No success dispatch: this only runs during SSR on a fresh store, so the slice stays at its initial []
        // and the section hides itself; disableServerCache keeps that empty page out of the HTML cache.
    }
};

export const fetchTransactionPrices = (_ctx: IFetchContext<IRPRequestMeta>) => async (dispatch: IAppDispatch, getState: () => IRPStore) => {
    if (!isServer) {
        return true;
    }

    await dispatch(loadTransactionPrices(getPricesAvailableDate(getState().report.price.pricesAvailableDate?.date)));

    return true;
};
import {endOfQuarter, formatISO, getQuarter, getYear, startOfQuarter, subQuarters} from "date-fns";

import type {IFetchContext} from "@pg-mono/data-fetcher";
import {consoleError} from "@pg-mono/logger";
import {createRequestActionTypes} from "@pg-mono/request-state";
import {PRICES_CITIES} from "@pg-rp/navigation";

import {isServer} from "../../../app/read_rp_environment_variables";
import type {IRPStore} from "../../../app/rp_reducer";
import type {IRPRequestMeta} from "../../../app/rp_request_meta";
import {disableServerCache} from "../../../app/state/server_cache_slice";
import {notifyBugsnag} from "../../../errors/bugsnag/notify_bugsnag";
import {IAppDispatch} from "../../../store/store";
import {getRcnDataApi} from "../api/get_rcn_data_api";
import {getPricesCityRegionId} from "../constants/prices_cities_regions";
import {
    TRANSACTION_PRICES_GRAIN,
    TRANSACTION_PRICES_MARKET,
    TRANSACTION_PRICES_METRIC,
    TRANSACTION_PRICES_MIN_TRANSACTIONS,
    TRANSACTION_PRICES_QUARTERS_LAG,
    TRANSACTION_PRICES_UNIT
} from "../constants/transaction_prices";
import type {ITransactionPrice} from "../types/ITransactionPrice";
import type {RcnPriceStatsFilters} from "../types/RcnPriceStatsQuery";
import type {RcnPriceStatsSeriesPoint} from "../types/RcnPriceStatsSeriesPoint";
import type {RcnRegionsPriceStatsQuery} from "../types/RcnRegionsPriceStats";
import {getPricesAvailableDate} from "../utils/get_prices_available_date";

const FETCH_TRANSACTION_PRICES = "reports/FETCH_TRANSACTION_PRICES";
export const fetchTransactionPricesTypes = createRequestActionTypes(FETCH_TRANSACTION_PRICES);

/**
 * Bounds of the single quarter the table reports: TRANSACTION_PRICES_QUARTERS_LAG quarters back from the quarter
 * of `date` - getPricesAvailableDate, so the quarter always lies inside BigData's offer history. The window is
 * that one quarter, so every city is read for the same span or not at all.
 */
export const getTransactionPricesDateRange = (date: Date): {date_from: string; date_to: string} => {
    const targetStart = subQuarters(startOfQuarter(date), TRANSACTION_PRICES_QUARTERS_LAG);

    return {
        date_from: formatISO(targetStart, {representation: "date"}),
        date_to: formatISO(endOfQuarter(targetStart), {representation: "date"})
    };
};

// The same quarter as an RCN period ("2026-04-01", "2026-Q2"), for views that pick the reported point out of a
// longer series - the city page reads this quarter too, so its figures match the hub table's row.
export const getTransactionPricesPeriod = (date: Date): {period_start: string; period_label: string} => {
    const targetStart = subQuarters(startOfQuarter(date), TRANSACTION_PRICES_QUARTERS_LAG);

    return {
        period_start: formatISO(targetStart, {representation: "date"}),
        period_label: `${getYear(targetStart)}-Q${getQuarter(targetStart)}`
    };
};

const isValidPoint = (point: unknown): point is RcnPriceStatsSeriesPoint => {
    if (typeof point !== "object" || point === null) {
        return false;
    }

    const candidate = point as Partial<RcnPriceStatsSeriesPoint>;

    return typeof candidate.period_start === "string" && typeof candidate.period_label === "string" && Number.isFinite(Number(candidate.value));
};

export const toTransactionPrice = (slug: string, regionId: number, point: RcnPriceStatsSeriesPoint): ITransactionPrice => ({
    slug,
    regionId,
    periodStart: point.period_start,
    periodLabel: point.period_label,
    medianPriceM2: Number(point.value),
    unit: TRANSACTION_PRICES_UNIT,
    transactionsCount: Number(point.n_rows) || 0,
    changePct: point.change_pct === null || point.change_pct === undefined ? null : Number(point.change_pct)
});

// The reported period, once it clears the sample floor. The series holds at most the one period the
// window asked for, so this is really a quality gate: too thin, and the city is left out entirely.
export const getLatestSettledPoint = (series: RcnPriceStatsSeriesPoint[]): RcnPriceStatsSeriesPoint | null => {
    const settled = series.filter((point) => isValidPoint(point) && Number(point.n_rows) >= TRANSACTION_PRICES_MIN_TRANSACTIONS);

    return settled.reduce<RcnPriceStatsSeriesPoint | null>(
        (latest, point) => (latest === null || point.period_start > latest.period_start ? point : latest),
        null
    );
};

// The reported quarter's params, shared by the hub table and the city page's single-region reads, so both
// show the same figure for a city. No rooms filter: the registry publishes an all-rooms aggregate, which rests
// on every sale in the period instead of one size's slice and matches the city-wide offer average.
export const getTransactionPricesBaseQuery = (date: Date): RcnPriceStatsFilters => ({
    grain: TRANSACTION_PRICES_GRAIN,
    market: TRANSACTION_PRICES_MARKET,
    unit: TRANSACTION_PRICES_UNIT,
    metric: TRANSACTION_PRICES_METRIC,
    order: "desc",
    // The window spans exactly one period, so there is at most one point to return.
    limit: 1,
    ...getTransactionPricesDateRange(date)
});

// regionIds must arrive sorted - they form part of the RTK Query cache key.
export const getTransactionPricesQuery = (regionIds: number[], date: Date): RcnRegionsPriceStatsQuery => ({
    regionIds,
    ...getTransactionPricesBaseQuery(date)
});

/**
 * Reads the reported quarter for every city from RCN and replaces the store slice the hub table renders.
 * Subscriptions are skipped - no component reads these entries, the derived slice below is what renders.
 */
export const loadTransactionPrices = (referenceDate: Date) => async (dispatch: IAppDispatch) => {
    dispatch({type: fetchTransactionPricesTypes.start});

    const cities = PRICES_CITIES.map((city) => ({slug: city.slug, regionId: getPricesCityRegionId(city.slug)})).filter(
        (city): city is typeof city & {regionId: number} => city.regionId !== null
    );
    // Deduplicated and sorted so the cache key is stable no matter how PRICES_CITIES is ordered.
    const regionIds = [...new Set(cities.map((city) => city.regionId))].sort((prev, next) => prev - next);

    try {
        const response = await dispatch(
            getRcnDataApi.endpoints.getRcnRegionsData.initiate(getTransactionPricesQuery(regionIds, referenceDate), {subscribe: false})
        ).unwrap();

        const seriesByRegion = new Map(response.results.map((result) => [result.region_id, result.series]));
        const result: ITransactionPrice[] = [];

        for (const city of cities) {
            // A region the registry has nothing for still comes back, with an empty series.
            const latestPoint = getLatestSettledPoint(seriesByRegion.get(city.regionId) ?? []);

            if (latestPoint) {
                result.push(toTransactionPrice(city.slug, city.regionId, latestPoint));
            }
        }

        dispatch({type: fetchTransactionPricesTypes.success, result});
    } catch (error) {
        consoleError("loadTransactionPrices error: ", error);
        notifyBugsnag(error, "loadTransactionPrices", {regionIds});
        dispatch(disableServerCache());

        // No success dispatch: this only runs during SSR on a fresh store, so the slice stays at its initial []
        // and the section hides itself; disableServerCache keeps that empty page out of the HTML cache.
    }
};

export const fetchTransactionPrices = (_ctx: IFetchContext<IRPRequestMeta>) => async (dispatch: IAppDispatch, getState: () => IRPStore) => {
    if (!isServer) {
        return true;
    }

    await dispatch(loadTransactionPrices(getPricesAvailableDate(getState().report.price.pricesAvailableDate?.date)));

    return true;
};
